import { T } from '@tolgee/react';
import { ReactNode } from 'react';

import theme from '../../../assets/theme';
import { PlanId } from './planData';
import {
  MATRIX_FREE_ACCENT,
  MATRIX_FREE_BG,
  MATRIX_GREEN_BG,
  MATRIX_INK,
  MATRIX_MUTED,
  MATRIX_ORANGE_ACCENT,
} from './pricingTokens';

/**
 * Data for the plan-overview matrix (design variant 4c, "US line-up") — a
 * bordered identity/price/action grid, distinct in shape from the
 * `staticPlanColumns` in `./planData` that back the full feature-comparison
 * table further down the page.
 *
 * 4c itself has no Premium column ("no Premium tier") — its sibling variant
 * 4a, from the same design turn, does include one, styled as a neutral
 * (uncoloured) column with an outline CTA. Premium's entry below is grafted
 * from 4a so the individual plan survives here, same as every redesign pass
 * before it.
 */

export type MatrixPlanCtaVariant = 'solidDark' | 'solidWhite' | 'outlineDark';

export type MatrixPlanCta = {
  label: ReactNode;
  href?: string;
  onClick?: () => void;
  variant: MatrixPlanCtaVariant;
};

export type MatrixPlanColumn = {
  id: PlanId;
  title: ReactNode;
  audience: ReactNode;
  /** Column background across the identity/price/action rows. */
  headerBg: string;
  /** Title colour on `headerBg`. */
  headerFg: string;
  /** Audience label + price-unit/caption colour on `headerBg`. */
  audienceFg: string;
  /** Premium substitutes its billing-toggle component here instead of a plain label. */
  price: ReactNode;
  /** Omitted when the visitor should be offered nothing at all. */
  cta?: MatrixPlanCta;
  /** Rendered in place of the CTA, e.g. "Your current plan". */
  ctaReplacement?: ReactNode;
  /** "Recommended" — Curriculum Alignment only. */
  badge?: ReactNode;
};

type StaticMatrixPlanColumn = Pick<
  MatrixPlanColumn,
  'id' | 'title' | 'audience' | 'headerBg' | 'headerFg' | 'audienceFg' | 'badge'
>;

export const staticMatrixPlanColumns: Record<PlanId, StaticMatrixPlanColumn> = {
  free: {
    id: 'free',
    title: <T keyName="Pricing.Plan.Free.Title" defaultValue="Free" />,
    audience: <T keyName="Pricing.Plan.Free.Audience" defaultValue="For every teacher" />,
    headerBg: MATRIX_FREE_BG,
    headerFg: MATRIX_INK,
    audienceFg: MATRIX_FREE_ACCENT,
  },
  premium: {
    id: 'premium',
    title: <T keyName="Pricing.Plan.Premium.Title" defaultValue="Premium" />,
    audience: (
      <T keyName="Pricing.Plan.Premium.Audience" defaultValue="For individual teachers" />
    ),
    headerBg: theme.colors.paleBeige,
    headerFg: MATRIX_INK,
    audienceFg: MATRIX_MUTED,
  },
  schoolDistrict: {
    id: 'schoolDistrict',
    title: <T keyName="Pricing.Plan.School.Title" defaultValue="School & District" />,
    audience: (
      <T keyName="Pricing.Plan.School.Audience" defaultValue="For schools & districts" />
    ),
    headerBg: theme.colors.orange,
    headerFg: MATRIX_INK,
    audienceFg: MATRIX_ORANGE_ACCENT,
  },
  curriculumAlignment: {
    id: 'curriculumAlignment',
    title: (
      <T
        keyName="Pricing.Plan.Curriculum.Title"
        defaultValue="ELA Curriculum Alignment"
      />
    ),
    audience: (
      <T keyName="Pricing.Plan.Curriculum.Audience" defaultValue="For districts" />
    ),
    headerBg: MATRIX_GREEN_BG,
    headerFg: theme.colors.white,
    audienceFg: theme.colors.white,
    badge: <T keyName="Pricing.Plan.Curriculum.Badge" defaultValue="Recommended" />,
  },
};

/* ─── Plan overview rows ──────────────────────────────────────────────────── */

const HMH_ALIGNMENT_URL =
  'https://docs.google.com/spreadsheets/d/14AJA7u-kNxoHFz9RiMBn1kSUBOFwC31gXrim-jwKe5s/edit?gid=1418225403#gid=1418225403';
const SAVVAS_ALIGNMENT_URL =
  'https://docs.google.com/spreadsheets/d/1q3Gk7oD4FdLaVvM1783Ume3fi3ToqJCyoN239wAFIZo/edit?gid=1418225403#gid=1418225403';
const BLUEBONNET_ALIGNMENT_URL =
  'https://docs.google.com/spreadsheets/d/1UB5jfznBNeHX37wDzLIk7_sR96jDC5fJY3JuUV4q_PE/edit?gid=1573170867#gid=1573170867';

export type MatrixOverviewRow = {
  id: string;
  label: ReactNode;
  cells: Partial<Record<PlanId, ReactNode>>;
};

export const matrixOverviewRows: MatrixOverviewRow[] = [
  {
    id: 'for-teachers',
    label: <T keyName="Pricing.Overview.ForTeachers" defaultValue="For Teachers" />,
    cells: {
      free: (
        <T
          keyName="Pricing.Overview.ForTeachers.Free"
          defaultValue="Weekly writing practice with instant Glow & Grow feedback"
        />
      ),
      premium: (
        <T
          keyName="Pricing.Overview.ForTeachers.Premium"
          defaultValue="Unlimited writing practice for ELA, Social Studies and Science (K-12)"
        />
      ),
      schoolDistrict: (
        <T
          keyName="Pricing.Overview.ForTeachers.School"
          defaultValue="Unlimited writing practice for ELA, Social Studies and Science (K-12)"
        />
      ),
      curriculumAlignment: (
        <T
          keyName="Pricing.Overview.ForTeachers.Curriculum"
          defaultValue="Find writing practice already built and aligned to your curriculum and scope and sequence"
        />
      ),
    },
  },
  {
    id: 'for-leaders',
    label: (
      <T
        keyName="Pricing.Overview.ForLeaders"
        defaultValue="For ELA Curriculum & Instruction Leaders"
      />
    ),
    cells: {
      schoolDistrict: (
        <T
          keyName="Pricing.Overview.ForLeaders.School"
          defaultValue="Track writing growth and spot skill gaps across every classroom and school"
        />
      ),
      curriculumAlignment: (
        <>
          <T
            keyName="Pricing.Overview.ForLeaders.Curriculum"
            defaultValue="Improve students' writing with practice aligned to your curriculum and scope & sequence."
          />
          <br />
          <T
            keyName="Pricing.Overview.ForLeaders.Curriculum.SampleLead"
            defaultValue="See sample alignment for"
          />{' '}
          <a href={HMH_ALIGNMENT_URL} target="_blank" rel="noopener noreferrer">
            HMH
          </a>
          ,{' '}
          <a href={SAVVAS_ALIGNMENT_URL} target="_blank" rel="noopener noreferrer">
            Savvas
          </a>
          ,{' '}
          <a href={BLUEBONNET_ALIGNMENT_URL} target="_blank" rel="noopener noreferrer">
            Bluebonnet
          </a>
        </>
      ),
    },
  },
];
